import { useEffect, useMemo, useState } from "react";
import { PlusCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { Spin, Modal, Button, Dropdown } from "antd";
import { useNavigate } from "react-router-dom";
import TableComponent, { DataTypeProps } from "../../components/TableComponent";
import MainButtonComponent from "../../components/MainButtonComponent";
import SearchComponent from "../../components/SearchComponent";
import { createColumn } from "../../utils/tableUtils";
import { tableDateFormatter } from "../../utils/formatDate";
import {
	HeaderComponent,
	Container,
	ContainerHeader,
	FiltersContainer,
	ActionButtonOverlay,
	StatusCell
} from "../../components/styled/common";
import {
	useVolunteersHighlightContext,
	VOLUNTEERS_HIGHLIGHT_LIST_PATH,
} from "../../context/VolunteersHighlightContext/VolunteersHighlightContext";
import { VolunteersHighlightListItem } from "../../context/VolunteersHighlightContext/constants";

const VolunteersHighlightList: React.FC = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [currentItem, setCurrentItem] = useState<VolunteersHighlightListItem | null>(null);
	const {
		volunteersHighlightList,
		clearVolunteersHighlightList,
		clearVolunteersHighlightFormData,
		getVolunteersHighlightList,
		removeVolunteersHighlight,
		operationStatus,
	} = useVolunteersHighlightContext();

	const [searchText, setSearchText] = useState("");

	const navigate = useNavigate();

	const tableData = useMemo(() => {
		const search = searchText.trim().toLocaleLowerCase("tr-TR");
		return search
			? volunteersHighlightList.filter(x => (x.title ?? "").toLocaleLowerCase("tr-TR").includes(search))
			: volunteersHighlightList;
	}, [volunteersHighlightList, searchText]);

	const isFetching = operationStatus.type === "loading" && operationStatus.operation === "fetch";
	const isDeleting = operationStatus.type === "loading" && operationStatus.operation === "delete";

	useEffect(() => {
		clearVolunteersHighlightList();
		clearVolunteersHighlightFormData();
		getVolunteersHighlightList();

		return () => {
			clearVolunteersHighlightList();
		};
	}, [clearVolunteersHighlightList, clearVolunteersHighlightFormData, getVolunteersHighlightList]);

	const columns = useMemo(() => [
		createColumn({
			title: "Sıra No",
			dataIndex: "displayOrder",
			width: 28,
			sorterType: "numeric",
		}),
		createColumn({
			title: "Başlık",
			dataIndex: "title",
			width: 80,
			sorterType: "string",
		}),
		createColumn({
			title: "Açıklama",
			dataIndex: "description",
			width: 110,
			sorterType: "string",
		}),
		createColumn({
			title: "Statü",
			dataIndex: "isActive",
			width: 35,
			sorterType: "boolean",
			render: (text, record) => (
				<StatusCell $bool={record.isActive}>{record.isActive ? "Aktif" : "Pasif"}</StatusCell>
			),
		}),
		createColumn({
			title: "Eklenme Tarihi",
			dataIndex: "creationDate",
			width: 45,
			sorterType: "string",
			render: (text, record) =>
				record.creationDate ? tableDateFormatter(record.creationDate, "DD.MM.YYYY") : "-",
		}),
		createColumn({
			title: "",
			dataIndex: "action",
			width: 20,
			render: (text, record) => {
				const item = record as VolunteersHighlightListItem;
				const menuItems = [
					{
						key: "edit",
						label: "Güncelle",
						onClick: () => navigate(`${VOLUNTEERS_HIGHLIGHT_LIST_PATH}/edit/${item.highlightId}`),
					},
					{
						key: "delete",
						label: "Sil",
						onClick: () => {
							setCurrentItem(item);
							setIsModalVisible(true);
						},
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
						<MoreOutlined />
					</Dropdown>
				);
			},
		}),
	], [navigate]);

	return (
		<div>
			<HeaderComponent>Öne Çıkanlar</HeaderComponent>
			<Container>
				<Spin spinning={isFetching}>
					<ContainerHeader style={{ justifyContent: "space-between" }}>
						<FiltersContainer>
							<SearchComponent
								placeholder="Başlık Ara"
								style={{ width: "16vw" }}
								value={searchText}
								onChange={setSearchText}
							/>
						</FiltersContainer>
						<ActionButtonOverlay>
							<MainButtonComponent
								style={{ marginRight: "15px" }}
								icon={<PlusCircleOutlined />}
								onClick={() => navigate(`${VOLUNTEERS_HIGHLIGHT_LIST_PATH}/add`)}
							>
								Yeni Ekle
							</MainButtonComponent>
						</ActionButtonOverlay>
					</ContainerHeader>
				</Spin>
				{!isFetching && (
					<TableComponent
						columns={columns}
						dataSource={tableData as unknown as DataTypeProps[]}
						rowKey="highlightId"
					/>
				)}
			</Container>
			<Modal
				title="Öne Çıkan İçeriği Sil"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				centered
				footer={[
					<Button key="back" type="text" onClick={() => setIsModalVisible(false)}>
						İptal
					</Button>,
					<Button
						loading={isDeleting}
						key="submit"
						type="primary"
						danger
						onClick={async () => {
							if (currentItem) {
								await removeVolunteersHighlight(currentItem.highlightId);
							}
							setIsModalVisible(false);
						}}
					>
						Evet
					</Button>,
				]}
			>
				<p><strong>{`"${currentItem?.title ?? ""}"`}</strong> başlıklı öne çıkan içeriği silmek istediğinizden emin misiniz?</p>
			</Modal>
		</div>
	);
};

export default VolunteersHighlightList;
